/**
 * @description 购物车 `chooseDiscountItemDto` 与商详限时折扣同源字段，组装「满额减 / 满件减 / 折」等短文案（用于数量下方提示）
 * @param {*} value - 数值或字符串
 * @param {{ maxDecimals?: number }} [opts]
 * @returns {string}
 */
const formatPromoNumber = (value, { maxDecimals = 2 } = {}) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (Number.isInteger(value)) return String(value)
    return String(Number(value.toFixed(maxDecimals)))
  }
  const raw = String(value).trim()
  if (!raw) return ''
  const parsed = Number(raw.replace(/[^\d.]/g, ''))
  if (!Number.isFinite(parsed)) return raw
  if (Number.isInteger(parsed)) return String(parsed)
  return String(Number(parsed.toFixed(maxDecimals)))
}

/**
 * @description 解析折数字段为展示用「几折」数字
 * @param {Object|null} d - 阶梯项或活动对象
 * @returns {string}
 */
const resolveZheText = (d) => {
  if (!d || typeof d !== 'object') return ''
  const raw = d.discount ?? d.discountValue ?? d.couponDiscount ?? ''
  const num = Number(String(raw).replace(/[^\d.]/g, ''))
  if (!Number.isFinite(num)) return ''
  let normalized = num
  if (normalized > 0 && normalized <= 1) normalized = normalized * 10
  if (normalized > 10 && normalized <= 100) normalized = normalized / 10
  return formatPromoNumber(normalized, { maxDecimals: 1 })
}

/**
 * @param {Object} d - 活动或 DTO
 * @param {number} rule - discountRule
 * @returns {string[]}
 */
const buildTagFromItems = (d, rule) => {
  const items = Array.isArray(d.discountItems) ? d.discountItems : []
  if (!items.length) return []

  const sorted = [...items].sort((a, b) => {
    const na = Number(a?.needAmount)
    const nb = Number(b?.needAmount)
    return (Number.isFinite(na) ? na : 0) - (Number.isFinite(nb) ? nb : 0)
  })

  const parts = []
  for (const it of sorted) {
    const needText = formatPromoNumber(it?.needAmount, { maxDecimals: 2 })
    if (!needText) continue

    if (rule === 0) {
      const amt = formatPromoNumber(it?.discount, { maxDecimals: 2 })
      if (amt) parts.push(`满${needText}元减${amt}元`)
      continue
    }
    if (rule === 1) {
      const amt = formatPromoNumber(it?.discount, { maxDecimals: 2 })
      if (amt) parts.push(`满${needText}台减${amt}元`)
      continue
    }
    if (rule === 2 || rule === 3) {
      const zhe = resolveZheText(it)
      if (zhe) {
        parts.push(rule === 3 ? `满${needText}台享${zhe}折` : `满${needText}元享${zhe}折`)
      }
    }
  }
  return parts
}

/**
 * @param {Object} d - 活动或 DTO
 * @param {number} rule - discountRule
 * @returns {string}
 */
const buildSingleFlat = (d, rule) => {
  const needRaw = d.needAmount ?? d.needNum ?? d.fullPiece ?? d.piece
  const needText = formatPromoNumber(needRaw, { maxDecimals: 2 })
  const zhe = resolveZheText(d)

  if (rule === 3 && needText && zhe) return `满${needText}台享${zhe}折`
  if (rule === 2 && needText && zhe) return `满${needText}元享${zhe}折`
  if (rule === 1) {
    const reduceRaw = d.discount ?? d.reduceAmount ?? d.discountAmount
    const reduceText = formatPromoNumber(reduceRaw, { maxDecimals: 2 })
    if (needText && reduceText) return `满${needText}台减${reduceText}元`
  }
  if (rule === 0) {
    const reduceRaw = d.discount ?? d.reduceAmount ?? d.discountAmount
    const reduceText = formatPromoNumber(reduceRaw, { maxDecimals: 2 })
    if (needText && reduceText) return `满${needText}元减${reduceText}元`
  }
  return ''
}

/**
 * @param {Object|null} d - chooseDiscountItemDto
 * @returns {string[]}
 */
const buildPromoTags = (d) => {
  if (!d || typeof d !== 'object') return []
  const rule = Number(d.discountRule)

  const fromItems = buildTagFromItems(d, rule)
  if (fromItems.length) return fromItems

  const single = buildSingleFlat(d, rule)
  if (single) return [single]

  if (rule === 0) return ['满减']
  return ['满N台M折']
}

const GENERIC_TAGS = new Set(['满减', '满N台M折'])

/**
 * @description 合并活动级 chooseDiscountItemDto 与行级 showDiscountInfoDto（行级优先生效满减展示字段）
 * @param {Object|null|undefined} chooseDto - discount.chooseDiscountItemDto
 * @param {Object|null|undefined} lineItem - shopCartItems 单项
 * @returns {Object|null|undefined}
 */
export function mergeShopCartMarkingDiscountDto(chooseDto, lineItem) {
  const lineShow = lineItem?.showDiscountInfoDto ?? lineItem?.show_discount_info_dto ?? null
  if (lineShow && typeof lineShow === 'object') {
    if (chooseDto && typeof chooseDto === 'object') {
      const restChoose = { ...chooseDto }
      delete restChoose.discountItems
      return { ...restChoose, ...lineShow }
    }
    return lineShow
  }
  return chooseDto
}

/**
 * @description 购物车行「限时折扣」数量下提示：优先阶梯/满减结构化文案，否则回退接口 discountName
 * @param {Object|null|undefined} dto - discount.chooseDiscountItemDto
 * @returns {string}
 */
export function buildShopCartMarkingDiscountSubtitle(dto) {
  if (!dto || typeof dto !== 'object') return ''
  const tags = buildPromoTags(dto)
  const meaningful = tags.filter((t) => t && !GENERIC_TAGS.has(t))
  if (meaningful.length) return meaningful.join('；')
  const name = typeof dto.discountName === 'string' ? dto.discountName.trim() : ''
  return name
}
